import clsx from 'clsx';
import {
    type ComponentPropsWithoutRef,
    createContext,
    type ForwardedRef,
    memo,
    type ReactNode,
    useId,
    useImperativeHandle,
    useState,
} from 'react';
import { usePress } from 'react-aria';
import { type ContextValue, useContextProps } from 'react-aria-components';

import { Icon } from '@gitbook/spine-icons';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { Checkbox } from '../Checkbox';
import { keyboardShortcutToTextArray } from '../KeyboardShortcut';
import { LoadingIndicatorIcon } from '../Loading';
import { type IconDefinition, renderIcon } from '../util';

export type MenuItemAPI = {
    /**
     * Run the menu item `onAction` and `onActionEnd`.
     */
    runAction: () => void;
};

type InternalMenuItemProps = {
    /**
     * Allow to get a ref to the Menu Item API.
     * Useful to run action.
     */
    apiRef?: React.RefObject<MenuItemAPI | null> | undefined;
    /**
     * Icon displayed at the left of the menu item.
     */
    icon?: IconDefinition;
    /**
     * Main text of the menu item.
     */
    children?: ReactNode;
    /**
     * Alternative text value if the node provided in children is not a string.
     */
    textValue?: string;
    /**
     * True if the menu item has the virtual focus.
     */
    isFocused?: boolean;
    /**
     * Indicates if the menu item can open a submenu and state of it.
     */
    subMenu?: 'focused' | 'open' | 'closed';
    /**
     * Index identifier used for virtualization.
     */
    'data-key'?: string;
    /**
     * Index identifier used for virtualization.
     */
    'data-index'?: number;
    /**
     * Mark the item as disabled.
     * Not focusable and not clickable.
     */
    disabled?: boolean;
    /**
     * Title prefix to be displayed before the title.
     */
    titlePrefix?: ReactNode;
    /**
     * Display a check at the end of the menu item.
     */
    checked?: boolean;
    /**
     * How the `checked` state is displayed: a trailing check glyph (`icon`, the default), or a
     * persistent row highlight with a bold title (`highlight`). Usually set once on the parent
     * `Menu` rather than per item. Ignored when `checkbox` is set.
     */
    checkedIndicator?: 'icon' | 'highlight';
    /**
     * Display a subtle title below the main text.
     */
    subtitle?: React.ReactNode;
    /**
     * Alternative text value if the node provided in subtitle is not a string.
     */
    subtitleTextValue?: string;
    /**
     * Whether the title and subtitle text should be truncated.
     * @default true
     */
    truncate?: boolean;
    /**
     * Display a suffix at the end of the menu item.
     */
    suffix?: ReactNode;
    /**
     * Display a keyboard shortcut at the end of the menu item.
     */
    keyboardShortcut?: string;
    /**
     * Additional keywords for completion.
     */
    keywords?: string[];
    /**
     * Pins the item ahead of fuzzy search results when filtering.
     */
    filterPriority?: number;
    /**
     * Display a checkbox at the start of the menu item, reflect the "checked" property.
     */
    checkbox?: boolean;
    /** Visibility of unchecked checkboxes; checked ones always remain visible. Defaults to `hover`. */
    checkboxVisibility?: 'hover' | 'always';
    /**
     * Callback run when the checkbox value changes.
     * Toggling the checkbox only runs this callback: it never runs `onAction`
     * nor closes the menu. It is up to the consumer to decide what happens
     * (e.g. keep the menu open for multi-select, or close it explicitly).
     */
    onCheckboxChange?: (value: boolean) => void;
    /**
     * Size of the menu item.
     * @default 'medium'
     */
    size?: 'medium' | 'large';
    /**
     * Reserve space for the icon when siblings contain one.
     */
    hasIconSibling?: boolean;
    /**
     * Callback run before the `onAction`.
     */
    onActionStart?: () => void;
    /**
     * Action executed when the menu item is pressed or activated using keyboard.
     */
    onAction?: (event: Event) => void | Promise<void>;
    /**
     * Callback run after the `onAction`.
     */
    onActionEnd?: () => void;
} & Omit<ComponentPropsWithoutRef<'div'>, 'children'>;

export const MenuItemContext = createContext<ContextValue<InternalMenuItemProps, HTMLDivElement>>(
    {}
);

export type MenuItemProps = Pick<
    InternalMenuItemProps,
    | 'className'
    | 'checked'
    | 'checkedIndicator'
    | 'disabled'
    | 'icon'
    | 'keyboardShortcut'
    | 'keywords'
    | 'filterPriority'
    | 'onAction'
    | 'children'
    | 'textValue'
    | 'subtitle'
    | 'subtitleTextValue'
    | 'truncate'
    | 'suffix'
    | 'checkbox'
    | 'checkboxVisibility'
    | 'onCheckboxChange'
    | 'titlePrefix'
    | 'hasIconSibling'
    | 'size'
>;

/** An action row in Menu; embedded checkboxes toggle independently without closing the menu. */
export function MenuItem(props: MenuItemProps) {
    return <InternalMenuItem {...props} />;
}

export function InternalMenuItem({
    ref,
    ...props
}: InternalMenuItemProps & { ref?: ForwardedRef<HTMLDivElement> }) {
    [props, ref] = useContextProps(props, ref, MenuItemContext);
    const {
        apiRef,
        icon: iconProp,
        className,
        isFocused,
        subMenu,
        disabled,
        titlePrefix,
        children,
        checked,
        checkedIndicator = 'icon',
        onActionStart,
        onAction,
        onActionEnd,
        suffix,
        subtitle,
        truncate: shouldTruncate = true,
        keywords,
        filterPriority: _filterPriority,
        keyboardShortcut,
        textValue,
        subtitleTextValue,
        checkbox,
        checkboxVisibility = 'hover',
        onCheckboxChange,
        hasIconSibling,
        size = 'medium',
        ...rest
    } = props;
    const titleId = useId();
    const [isLoading, setIsLoading] = useState(false);
    const runAction = useEventCallback((options?: { closeOnActionEnd?: boolean }) => {
        if (isLoading) {
            return;
        }

        onActionStart?.();

        const event = new Event('action', { cancelable: true, bubbles: false });
        const value = onAction?.(event);

        if (value instanceof Promise) {
            setIsLoading(true);
            value.finally(() => {
                setIsLoading(false);
                if (onAction && options?.closeOnActionEnd !== false && !event.defaultPrevented) {
                    onActionEnd?.();
                }
            });
            return;
        }

        if (onAction && options?.closeOnActionEnd !== false && !event.defaultPrevented) {
            onActionEnd?.();
        }
    });
    useImperativeHandle(apiRef, () => ({ runAction }), [runAction]);
    const runCheckboxAction = useEventCallback((value: boolean) => {
        // Toggling the checkbox only notifies the consumer through `onCheckboxChange`.
        // It deliberately does not run `onAction` nor close the menu, so the consumer is
        // free to keep the menu open for multi-select or close it explicitly.
        onCheckboxChange?.(value);
    });
    const runPressAction = useEventCallback(() => {
        // Pressing the row runs the regular action path (and closes the menu unless the
        // action prevents it), independently from the checkbox control.
        runAction();
    });
    const { pressProps, isPressed } = usePress({
        onPress: runPressAction,
        isDisabled: disabled,
        preventFocusOnPress: true,
    });
    const icon = isLoading && iconProp ? LoadingIndicatorIcon : iconProp;

    // A checked row in `highlight` mode is marked by a persistent fill + bold title instead of a
    // trailing glyph. It paints through the Current Background variable like the focus state, but
    // with the stronger `active` token so it stays distinguishable from the focus fill.
    const isHighlighted = Boolean(checked) && !checkbox && checkedIndicator === 'highlight';

    return (
        <div
            ref={ref}
            role="menuitem"
            tabIndex={-1}
            data-focused={isFocused ? '' : undefined}
            data-pressed={isPressed ? '' : undefined}
            className={clsx(
                'group/menu-item',
                'relative flex max-w-128 min-w-48',
                size === 'large' ? 'px-4 py-3' : 'py-1.5 pr-3 pl-3.5',
                'cursor-pointer whitespace-nowrap text-base select-none',
                // Current Background pattern: set `--current-background` per
                // state and paint from it, so the row's visible fill and the
                // value children read are the same single source. Bordered
                // descendants (e.g. `inStack` avatar borders) camouflage with
                // the row. See Recipes/Patterns/Current Background.
                'data-[pressed=true]:bg-(--current-background) data-[pressed=true]:[--current-background:var(--neutral-ui-bg-active)]',
                'aria-disabled:cursor-default aria-disabled:opacity-disabled',
                'before:absolute before:inset-0 before:inset-x-1 before:z-0 before:block before:rounded-sm before:content-[""]',
                isHighlighted
                    ? 'font-semibold [--current-background:var(--neutral-ui-bg-active)] before:bg-(--current-background)'
                    : isFocused
                      ? '[--current-background:var(--neutral-ui-bg)] before:bg-(--current-background)'
                      : 'before:bg-transparent',
                className
            )}
            aria-disabled={disabled}
            {...pressProps}
            {...rest}
        >
            <div className="z-[1] inline-flex min-w-0 grow items-center gap-2">
                {checkbox && (
                    <span
                        className={clsx(
                            'flex shrink-0',
                            !checked &&
                                checkboxVisibility === 'hover' &&
                                'opacity-0 group-hover/menu-item:opacity-100 group-data-[focused]/menu-item:opacity-100'
                        )}
                    >
                        <Checkbox
                            aria-label={textValue}
                            aria-labelledby={textValue ? undefined : titleId}
                            size="small"
                            disabled={disabled}
                            value={checked}
                            stopPropagation
                            onChange={(value) => {
                                runCheckboxAction(value);
                            }}
                        />
                    </span>
                )}
                {icon && (
                    <span className={clsx('contents', !isFocused && 'text-muted')}>
                        {renderIcon(icon, {
                            size: 16,
                            className: clsx('-mt-px shrink-0'),
                        })}
                    </span>
                )}
                {!icon && hasIconSibling ? (
                    <span aria-hidden className="-mt-px size-4 shrink-0" />
                ) : null}
                <span
                    className={clsx('flex min-w-0 flex-1 flex-col', shouldTruncate && 'truncate')}
                >
                    <span id={titleId} className={clsx(shouldTruncate && 'truncate')}>
                        {titlePrefix}
                        {children}
                    </span>
                    {subtitle ? (
                        <span
                            className={clsx(
                                'text-ui-small text-muted',
                                shouldTruncate ? 'truncate' : 'text-wrap whitespace-normal'
                            )}
                        >
                            {subtitle}
                        </span>
                    ) : null}
                </span>

                {suffix}

                {keyboardShortcut ? <Shortcut shortcut={keyboardShortcut} /> : null}

                {checked && !checkbox && checkedIndicator === 'icon' && (
                    <span
                        className={clsx(
                            'w-4 text-right text-[1rem] leading-5 font-medium',
                            !isFocused && 'text-muted'
                        )}
                    >
                        ✓
                    </span>
                )}

                {subMenu && (
                    <Icon.Caret
                        className={clsx(
                            'size-4 text-right text-[0.5rem]',
                            {
                                closed: 'text-light',
                                open: 'text-muted',
                                focused: 'text-base',
                            }[subMenu]
                        )}
                    />
                )}
            </div>
        </div>
    );
}

export type MenuItemTitlePrefixProps = { children: React.ReactNode };

export function MenuItemTitlePrefix(props: MenuItemTitlePrefixProps) {
    const { children } = props;
    return (
        <span className="contents text-muted">
            {children}
            <Icon.ChevronRight className="mx-1 -mt-px size-4 shrink-0" />
        </span>
    );
}

const Shortcut = memo(function Shortcut(props: { shortcut: string }) {
    const array = keyboardShortcutToTextArray(props.shortcut);
    if (array.length === 0) {
        return null;
    }
    return (
        <span className="space-x-1 leading-none">
            {array.map((value, index) => (
                <kbd
                    key={index}
                    className="min-w-2.5 text-center font-[inherit] text-[0.6875rem] text-secondary"
                >
                    {value}
                </kbd>
            ))}
        </span>
    );
});
